import { Button, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ObjectSchema } from 'yup';
import { formAutocomplete } from '../../../config';
import { uiStateSelectors } from '../../../services/redux/features/uiState.slice';
import { MixedComponentsProps } from '../../../types/mixedComponentProps';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from './formInputs';
import { TradingDetailsServicePlanBusinessFormModel_MX } from './formModel';
import { tradingDetailsInitialValues } from './initialValues';
import { createTradingDetailsServicePlanBusinessFormValidator } from './validationSchema';
import { FormHelpers } from '../../../screens/utils';
import { BusinessCustomerTypesEnum } from '../../../screens/NewQuotation/CustomerDataPage/customerDataPage.model';
import { journeyDataSelectors } from '../../../services/redux/features/journeyData.slice';

const croOptionalFor = [BusinessCustomerTypesEnum.partnership, BusinessCustomerTypesEnum.other];

export const TradingDetailsServicePlanBusinessFormNew_MX: React.FC<MixedComponentsProps> = ({
  formHelpers,
  titleTranslationKey,
}) => {
  const [initialValues] = useState(tradingDetailsInitialValues);
  const businessType = useSelector(journeyDataSelectors.businessCustomerType);
  const [validationSchema] = useState<ObjectSchema<Partial<TradingDetailsServicePlanBusinessFormModel_MX>>>(() =>
    createTradingDetailsServicePlanBusinessFormValidator(!croOptionalFor.includes(businessType!))
  );

  const { formSectionProps, onFormSubmitSuccess, onStartEditInSummaryMode, isDisabledFormSummary } = formHelpers;

  const onSubmit = (values: TradingDetailsServicePlanBusinessFormModel_MX) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(
        t(titleTranslationKey ?? `tradingDetailsServicePlanBusinessForm:title:mixed`),
        isDisabledFormSummary
      )}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent disabled={isDisabledFormSummary} formHelpers={formHelpers} />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = {
  disabled: boolean;
  formHelpers: FormHelpers;
};

export const FormContent = (props: FormContentProps) => {
  const { submitForm, values } = useFormikContext<TradingDetailsServicePlanBusinessFormModel_MX>();
  const { disabled, formHelpers } = props;
  const { onFormValuesChange } = formHelpers;

  const snCSubmitted = !useSelector(uiStateSelectors.snCSubmittedMock);
  const disableAllFormControls = disabled || snCSubmitted;

  const [inputs, updateInputs] = useState<ReturnType<typeof createFormInputs>>(() =>
    createFormInputs(disableAllFormControls)
  );

  useEffect(() => {
    updateInputs(createFormInputs(disableAllFormControls));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableAllFormControls]);

  useEffect(() => {
    onFormValuesChange(values);
  }, [values, onFormValuesChange]);

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3">
            <Button disabled={disableAllFormControls} type="button" secondary icon="address-book" ariaLabel="Search existing customer" onClick={() => ''}>
              {t('tradingDetailsServicePlanBusinessForm:searchExistingCustomer')}
            </Button>
            </Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.croNumber()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.croNumberButton(!values.croNumber)}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.tradingAs()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.legalName()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.vatLiable()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.vatNumber()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.email()}</Layout.Item>
            <Layout.Item default="1/4" s="1/1">{inputs.countryCode()}</Layout.Item>
            <Layout.Item default="1/4" s="1/1">{inputs.mobileNumber()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.correspondenceMethod()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
        <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers} />
      </Form>
    );
};
